import { getRecoil, setRecoil } from 'recoil-nexus';

import { noteHasContent } from 'components/practice/charts/ClinicalNote/MyScribeAI/Templates/AmbientListening/lib/noteContent';
import {
  ambientListeningIntelligenceChatId,
  ambientListeningMessages,
  ambientListeningNoteTitle,
  ambientListeningRegenerateTargetId,
  ambientListeningStopped,
  ambientListeningStreamingText,
  currentActiveStepID,
  emCPTsText,
  enhanceAmbientListeningLoading,
  enhancedNote,
  preSelectedNoteType
} from 'components/practice/charts/ClinicalNote/MyScribeAI/Templates/AmbientListening/state';

import { assembleChunks, clearChunkBuffer } from './chunkAssembly';
import { isFollowUpFromConversations, stripNoteFlags } from './helpers';

let lastProcessedChatId = null;

const chunkBuffers = {};

const upsertMessage = (newMessage) => {
  if (!newMessage?.id) return;
  const existing = getRecoil(ambientListeningMessages) || [];
  const idx = existing.findIndex((m) => m?.id === newMessage.id);
  if (idx === -1) {
    setRecoil(ambientListeningMessages, [...existing, newMessage]);
    return;
  }
  const next = existing.slice();
  next[idx] = { ...existing[idx], ...newMessage };
  setRecoil(ambientListeningMessages, next);
};

const updateAmbientListening = (data) => {
  const {
    done = false,
    abort = false,
    note,
    noteType,
    isNote = true,
    emCPTsText: emCodesText,
    isDescription = false,
    response,
    intelligenceChatMessage,
    ...rest
  } = data;

  try {
    if (rest?.intelligence_chat_id) {
      setRecoil(ambientListeningIntelligenceChatId, rest?.intelligence_chat_id);
    }

    if (isDescription) {
      const title = rest?.intelligenceChat?.name;
      if (title) setRecoil(ambientListeningNoteTitle, title);
      return;
    }

    // Drop late streaming chunks once the user aborted this turn — without this
    // they keep re-setting streamingText/loading, so the UI types to the end.
    if (!done && !abort && getRecoil(ambientListeningStopped)) return;

    if (!done && !abort && !getRecoil(enhanceAmbientListeningLoading)) return;

    if (!done && !abort && response) {
      if (isNote) {
        setRecoil(enhanceAmbientListeningLoading, true);
        return;
      }

      const isFollowUp = isFollowUpFromConversations(getRecoil(ambientListeningMessages));

      if (!isFollowUp || rest?.isRegenerate) {
        setRecoil(enhanceAmbientListeningLoading, true);
        return;
      }

      let assembled = null;
      if (typeof response === 'object' && 'value' in response) {
        assembled = assembleChunks(
          chunkBuffers,
          rest?.intelligence_chat_id,
          response.value,
          response.orderId
        );
      } else if (typeof response === 'string' && response) {
        assembled = response;
      }
      if (assembled != null) {
        setRecoil(ambientListeningStreamingText, stripNoteFlags(assembled));
        setRecoil(enhanceAmbientListeningLoading, true);
      }
      return;
    }

    if (!done && !abort) return;

    if (rest?.intelligence_chat_id) clearChunkBuffer(chunkBuffers, rest.intelligence_chat_id);

    const isLoading = getRecoil(enhanceAmbientListeningLoading);

    const hasPartial = abort && !!intelligenceChatMessage;
    if (!isLoading && !hasPartial && rest?.intelligence_chat_id === lastProcessedChatId) return;
    lastProcessedChatId = rest?.intelligence_chat_id || null;

    // Treat the turn as aborted when the user hit stop (ambientListeningStopped),
    // not just when the event carries abort=true: the worker generates ahead of
    // what it streams, so it can finish and emit done=true (full) right as stop
    // is clicked. On abort, keep what actually streamed — the worker's
    // finalResponse (persisted message / parsed note) is fuller than what the
    // user saw — matching auto_map, which renders the assembled streamed chunks.
    const wasAborted = abort || getRecoil(ambientListeningStopped);
    const streamedPartial = wasAborted ? getRecoil(ambientListeningStreamingText) : null;

    setRecoil(preSelectedNoteType, noteType);
    setRecoil(enhanceAmbientListeningLoading, false);
    setRecoil(ambientListeningStreamingText, null);
    if (!wasAborted) setRecoil(ambientListeningStopped, false);

    if (!wasAborted && noteHasContent(note)) setRecoil(enhancedNote, note);
    if (emCodesText) setRecoil(emCPTsText, emCodesText);

    if (intelligenceChatMessage) {
      // Aborted partials are not a finished note — render them as plain text,
      // never as a structured note preview.
      upsertMessage(
        streamedPartial
          ? { ...intelligenceChatMessage, message: streamedPartial, isNote: false, note: null }
          : intelligenceChatMessage
      );
    }

    setRecoil(ambientListeningRegenerateTargetId, null);

    if (!isLoading) return;

    const isFollowUp = isFollowUpFromConversations(getRecoil(ambientListeningMessages));
    if (abort) {
      setRecoil(currentActiveStepID, isFollowUp ? 3 : 2);
    } else if (!isFollowUp) {
      setRecoil(currentActiveStepID, 3);
    }
  } catch (error) {
    console.error('Error', error);
  }
};

export default updateAmbientListening;
